import { Bounds, CanvasCoordinates, CanvasRectangle } from './abstract-canvas-decorator';

export const DEFAULT_STROKE_WIDTH = 0.25;
export const HIGHLIGHTED_STROKE_WIDTH = 3;
export const HIGHLIGHTED_STYLE = '#c5c5c5';
export const GREY_BOX_FILL = '#808080';
export const LIGHT_GREY_BOX_FILL = '#e0dddd';

export class Object2d {
  public bounds: Bounds;
  constructor(public selected: boolean) {}
  inBounds(canvasSelectionCoords: CanvasCoordinates): boolean {
    return inBounds(this.bounds, canvasSelectionCoords);
  }
}

export const scalarProduct = (a: CanvasCoordinates, b: CanvasCoordinates): number => {
  return a.x * b.x + a.y * b.y;
};

export const absoluteValue = (v: CanvasCoordinates): number => {
  return Math.sqrt(Math.pow(v.x, 2) + Math.pow(v.y, 2));
};

export const angle = (a: CanvasCoordinates, b: CanvasCoordinates): number => {
  const cos = (scalarProduct(a, b) / absoluteValue(a)) * absoluteValue(b);
  return Math.cos(cos);
};

export const vectorOf = (a: CanvasCoordinates, b: CanvasCoordinates): CanvasCoordinates => {
  return { x: b.x - a.x, y: b.y - a.y };
};

export const perpendicularOf = (a: CanvasCoordinates): CanvasCoordinates => {
  return { x: -a.y, y: a.x };
};

export const drawStrokeRect = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number): void => {
  ctx.beginPath();
  ctx.rect(x, y, w, h);
  ctx.stroke();
  ctx.fill();
  ctx.closePath();
};

export const drawLine = (ctx: CanvasRenderingContext2D, x1: number, y1: number, x2: number, y2: number): void => {
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(x1, y1);
  ctx.lineTo(x2, y2);
  ctx.stroke();
  ctx.closePath();
  ctx.restore();
};

export const inBounds = (bounds: Bounds, coords: CanvasCoordinates): boolean => {
  return coords.x >= bounds.minX && coords.x <= bounds.maxX && coords.y >= bounds.minY && coords.y <= bounds.maxY;
};

export const boundsToRectangle = (bounds: Bounds): CanvasRectangle => {
  return { x: bounds.minX, y: bounds.minY, w: bounds.width, h: bounds.height };
};

export const centerOfBounds = (bounds: Bounds): CanvasCoordinates => {
  return { x: bounds.minX + bounds.width / 2, y: bounds.minY + bounds.height / 2 };
};

export const normalize = (v: CanvasCoordinates): CanvasCoordinates => {
  const l = absoluteValue(v);
  return scaleVector(v, 1 / l);
};

export const scaleVector = (a: CanvasCoordinates, scale: number): CanvasCoordinates => {
  return {
    x: a.x * scale,
    y: a.y * scale,
  };
};

export const getPointOfStraightLine = (a: CanvasCoordinates, direction: CanvasCoordinates, distance: number): CanvasCoordinates => {
  const v: CanvasCoordinates = scaleVector(direction, distance);

  return {
    x: a.x + v.x,
    y: a.y + v.y,
  };
};

export const drawLineWithTicks = (
  ctx: CanvasRenderingContext2D,
  origin: CanvasCoordinates,
  end: CanvasCoordinates,
  color = 'black',
  lineDirection: 'x' | 'y',
  fontSize: number,
  lineWidth = 2,
  tickWidth?: { origin: number; end: number; hideOriginTick?: boolean },
  textPosition = 0,
  tickSpacing = 0,
  verticalText = false,
): void => {
  const hideOriginTick = !!tickWidth?.hideOriginTick;
  ctx.save();
  ctx.lineWidth = lineWidth;
  ctx.strokeStyle = color;
  ctx.font = `${fontSize}px 'Open Sans', monospace`;
  ctx.textAlign = 'center';
  ctx.beginPath();
  ctx.moveTo(origin.x, origin.y);
  const centerY = (origin.y + end.y) / 2;

  // For drawing in between two lines in the y direction
  if (lineDirection === 'y') {
    const beamToBeamLength = end.y - origin.y;
    drawText(ctx, `${beamToBeamLength}`, origin.x + textPosition, centerY, verticalText);
    ctx.lineTo(end.x, end.y);
    ctx.stroke();
    ctx.closePath();
    ctx.beginPath();
  }

  if (lineDirection === 'x') {
    ctx.lineTo(end.x, end.y);
    ctx.stroke();
    ctx.closePath();
  }

  if (textPosition && lineDirection === 'x') {
    const length = end.x - origin.x;
    drawText(ctx, `${Math.round(length * 100) / 100}`, (origin.x + end.x) / 2, centerY + textPosition);
  }

  const direction = vectorOf(origin, end);
  if (!hideOriginTick) {
    // Draw origin tick
    drawTick(ctx, origin, direction, tickWidth, tickSpacing);
  }

  // Draw end tick
  drawTick(ctx, end, direction, tickWidth, tickSpacing);

  ctx.restore();
};

export const drawText = (ctx: CanvasRenderingContext2D, text: string, x: number, y: number, isVertical = false): void => {
  ctx.save();
  ctx.translate(x, y);
  ctx.scale(1, -1);
  if (isVertical) {
    ctx.rotate(-Math.PI / 2);
  }
  ctx.fillText(text, 0, 0);
  ctx.restore();
};

export const drawTick = (ctx: CanvasRenderingContext2D, coordinates: CanvasCoordinates, direction: CanvasCoordinates, tickWidth = { origin: 5, end: 5 }, tickSpacing = 0): void => {
  const tickDirection = perpendicularOf(direction);
  const startOfTick = getPointOfStraightLine(coordinates, normalize(tickDirection), -tickWidth.origin);
  const endOfTick = getPointOfStraightLine(coordinates, normalize(tickDirection), tickWidth.end);
  let xOffset = 0;
  let yOffset = 0;
  // Find which direction to apply spacing to.
  if (startOfTick.y === endOfTick.y) {
    xOffset = tickSpacing;
  } else {
    yOffset = tickSpacing;
  }
  ctx.beginPath();
  ctx.moveTo(startOfTick.x + xOffset, startOfTick.y + yOffset);
  ctx.lineTo(endOfTick.x + xOffset, endOfTick.y + yOffset);
  ctx.stroke();
  ctx.closePath();
};
